import React, { useState, useLayoutEffect, useRef, useMemo } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../hooks/useScreenSize";
import {
  formatDateWithDay,
  getAmChartsLocale,
  processPriceData,
  shouldShowYear,
} from "../../../utils/chartDataProcessors";
import { useLanguage } from "../../../context/LanguageContext";
import GroupByDropdown from "./GroupByDropdown/GroupByDropdown";
import SelectCurrencyOverlay from "./SelectCurrencyOverlay/SelectCurrencyOverlay";

const AnalyticsByCompareOrderPrice = ({ item, currency }) => {
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();
  const [groupBy, setGroupBy] = useState("day");

  const processedData = useMemo(() => {
    if (groupBy === "day") {
      const showYear = shouldShowYear(item);

      return item
        .map((dataItem) => {
          const [day, month, year] = dataItem.date.split(".");
          const isoDate = new Date(year, month - 1, day);

          return {
            ...dataItem,
            month: formatDateWithDay(isoDate, currentLanguage, showYear),
            tooltipText: formatDateWithDay(isoDate, currentLanguage, showYear).replace("\n", " "),
            isoDate,
          };
        })
        .sort((a, b) => a.isoDate - b.isoDate);
    } else {
      return processPriceData(item, currentLanguage);
    }
  }, [item, currentLanguage, groupBy]);

  useLayoutEffect(() => {
    let root = am5.Root.new("chartdiv-compare");
    root.setThemes([am5themes_Animated.new(root)]);

    const locale = getAmChartsLocale(currentLanguage);
    if (locale) {
      root.locale = locale;
    }

    root._logo.dispose();

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        wheelY: "zoomX",
      }),
    );

    if (isMobileView) {
      chart.set("paddingLeft", 1);
    }
    chart.set("wheelable", true);

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "month",
        groupData: true,
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: groupBy === "day" ? 60 : 50,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
        }),
        tooltip: am5.Tooltip.new(root, {
          labelText: groupBy === "day" ? "{tooltipText}" : "{month}",
        }),
      }),
    );
    xAxis.data.setAll(processedData);

    if (groupBy === "day") {
      xAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#8E8E8E"),
        fontSize: 12,
        rotation: 0,
        multiline: true,
        textAlign: "center",
        centerX: am5.percent(50),
        maxWidth: 70,
        oversizedBehavior: "wrap",
      });
    } else {
      xAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#8E8E8E"),
        fontSize: 12,
        rotation: 0,
        multiline: true,
        textAlign: "center",
        centerX: am5.percent(50),
        maxWidth: 50,
        oversizedBehavior: "wrap",
      });
    }

    xAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      }),
    );

    yAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
    });

    let priceSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: "Price",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "price",
        categoryXField: "month",
        strokeWidth: 2.29,
        stroke: am5.color(0x4e7cff),
        fill: am5.color(0x4e7cff),
        fillOpacity: 0.2,
      }),
    );
    priceSeries.data.setAll(processedData);

    priceSeries.strokes.template.setAll({
      strokeWidth: 2.29,
    });

    priceSeries.fills.template.setAll({
      visible: true,
      fillOpacity: 0.2,
    });

    let algoPriceSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: "AlgoPrice",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "algoPrice",
        categoryXField: "month",
        stroke: am5.color(0x22c0ff),
        fill: am5.color(0x22c0ff),
        fillOpacity: 0.1,
      }),
    );
    algoPriceSeries.data.setAll(processedData);

    algoPriceSeries.strokes.template.setAll({
      strokeWidth: 2.29,
    });

    algoPriceSeries.fills.template.setAll({
      visible: true,
      fillOpacity: 0.1,
    });

    let cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomX",
      }),
    );
    cursor.lineY.set("visible", false);

    priceSeries.set(
      "tooltip",
      am5.Tooltip.new(root, {
        labelText: "Цена: {valueY}",
      }),
    );

    algoPriceSeries.set(
      "tooltip",
      am5.Tooltip.new(root, {
        labelText: "Алго цена: {valueY}",
      }),
    );

    return () => {
      root.dispose();
    };
  }, [item, isMobileView, processedData, currentLanguage, groupBy]);

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>{t("analytics.priceCompare")}</span>
        <GroupByDropdown groupBy={groupBy} setGroupBy={setGroupBy} />
      </div>

      <div className={style.area} style={{ position: "relative" }}>
        <SelectCurrencyOverlay show={currency === "all" || currency === ""} />
        <div id="chartdiv-compare" className={style.area_chart}></div>
        <div className={style.area_names}>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: "#4E7CFF" }}></div>
            <div className={style.area_type}>{t("analytics.yourPrice")}</div>
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: "#22c0ff" }}></div>
            <div className={style.area_type}>{t("analytics.algoPrice")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsByCompareOrderPrice;
